import {
    getInvoiceAccentColor,
    getInvoiceBorderColor,
    getInvoiceFontStack,
    getInvoiceMutedTextColor,
    getInvoiceSubtleTextColor,
    getInvoiceTextColor,
    INVOICE_PDF_FONTS_STYLESHEET_URL,
    normalizeHexColor,
} from "../constants/invoiceDesign";
import {
    NATIVE_INVOICE_DEFAULT_PAGE_SIZE,
    NATIVE_INVOICE_DEFAULT_SHOW_PAGINATION,
    type NativeInvoiceAdjustmentSign,
    type NativeInvoiceFooterAlign,
    type NativeInvoiceLabelKey,
    type NativeInvoicePageSize,
} from "../constants/nativeInvoice";
import type { NativeInvoicePdfFieldSettings } from "../schemas/domains/invoice/native-invoice-field-settings.schema";
import type { NativeInvoiceParty } from "../types/nativeInvoice";
import { applyNativeInvoicePdfFieldSettings } from "./applyNativeInvoicePdfFieldSettings";
import {
    resolveNativeInvoiceTermLabel,
    type NativeInvoiceCustomTermValue,
    type NativeInvoiceTermType,
} from "./invoice-terms.helper";
import {
    deriveInvoiceMonogram,
    formatInvoiceDate,
    formatInvoiceMoney,
    formatInvoiceQuantity,
    getInvoiceAddressLines,
    getInvoiceTotalsVisibility,
    resolveNativeInvoiceLabels,
    type InvoiceAddressLineLabels,
} from "./nativeInvoice";
import {
    countNativeInvoicePartyLines,
    nativeInvoicePageBoxCss,
    paginateNativeInvoiceDocument,
    type NativeInvoicePageBoxCss,
} from "./nativeInvoicePagination";
import {
    renderNativeInvoiceDocumentPage,
    type NativeInvoiceDocumentLineView,
    type NativeInvoiceDocumentPartyView,
} from "./nativeInvoiceDocumentMarkup";
import { nativeInvoiceDocumentCss } from "../styles/nativeInvoiceDocumentCss";

export interface NativeInvoiceDocumentLineInput {
    productOrService?: string | null;
    description?: string | null;
    quantity?: number | null;
    unitAmount?: number | null;
    amount: number;
    // Per-line Tax/Discount/Deposit values, only meaningful (non-zero) when
    // the matching isXPerLineItem calculation flag is on — see
    // NativeInvoiceItemsTableColumnVisibility for how these feed the optional
    // per-line columns.
    tax?: number;
    discount?: number;
    deposit?: number;
}

export interface NativeInvoiceDocumentRenderInput {
    currency: string;
    invoiceNumber?: string | null;
    invoiceDate?: string | null;
    dueDate?: string | null;
    poNumber?: string | null;
    // Structured Terms dropdown selection (PRO-11287), resolved here to its
    // display string (e.g. "Net 30") for the info-grid's Terms field
    // (PRO-12178). Not printed before this field existed; distinct from
    // `terms` below (the free-text "Payment instructions" field). `customTerm`
    // carries the underlying rule for a Custom selection so the Terms field
    // prints the actual rule (e.g. "Due in 45 days") instead of a bare "Custom".
    termType?: NativeInvoiceTermType | null;
    customTerm?: NativeInvoiceCustomTermValue | null;
    from: NativeInvoiceParty;
    to: NativeInvoiceParty;
    lines: NativeInvoiceDocumentLineInput[];
    pageSize?: NativeInvoicePageSize;
    footer?: string | null;
    footerAlign?: NativeInvoiceFooterAlign | null;
    showPagination?: boolean;
    subtotal: number;
    total: number;
    tax: number;
    discount: number;
    deposit: number;
    taxSign?: NativeInvoiceAdjustmentSign | null;
    discountSign?: NativeInvoiceAdjustmentSign | null;
    depositSign?: NativeInvoiceAdjustmentSign | null;
    isTaxPerLineItem?: boolean;
    isDiscountPerLineItem?: boolean;
    isDepositPerLineItem?: boolean;
    isTaxDisplayedPerLineItem?: boolean;
    isDiscountDisplayedPerLineItem?: boolean;
    isDepositDisplayedPerLineItem?: boolean;
    note?: string | null;
    terms?: string | null;
    backgroundColor?: string | null;
    accentColor?: string | null;
    textColor?: string | null;
    fontFamily?: string | null;
    logoUrl?: string | null;
    monogram?: string | null;
    initials?: string | null;
    labelOverrides?: Partial<Record<NativeInvoiceLabelKey, string>> | null;
    fieldSettings?: NativeInvoicePdfFieldSettings | null;
    senderEmail?: string | null;
}

export interface NativeInvoiceDocumentRenderResult {
    html: string;
    pageCount: number;
    pageBox: NativeInvoicePageBoxCss;
}

function formatSignedMoney(
    amount: number,
    sign: NativeInvoiceAdjustmentSign,
    currency: string,
): string {
    return sign === "negative"
        ? `-${formatInvoiceMoney(Math.abs(amount), currency)}`
        : formatInvoiceMoney(Math.abs(amount), currency);
}

function footerAlignCss(
    align: NativeInvoiceFooterAlign | null | undefined,
): NativeInvoiceFooterAlign {
    if (align === "left" || align === "right") {
        return align;
    }
    return "center";
}

interface BuildPartyViewParams {
    party: NativeInvoiceParty;
    showLabel: boolean;
    sender: string;
    addressLabels?: InvoiceAddressLineLabels;
}

function buildPartyView({
    party,
    showLabel,
    sender,
    addressLabels,
}: BuildPartyViewParams): NativeInvoiceDocumentPartyView {
    const name = party.businessName ?? party.name ?? party.email ?? "";
    const fullName =
        party.name != null && party.name !== name ? party.name : "";
    const company =
        party.company != null && party.company !== "" && party.company !== name
            ? party.company
            : "";
    const showEmail = party.email != null && party.email !== name;
    return {
        name,
        fullName,
        company,
        email: party.email ?? "",
        showEmail,
        phoneNumber: party.phoneNumber ?? "",
        taxId: party.taxId ?? "",
        addressLines: getInvoiceAddressLines(party.address, addressLabels),
        showLabel,
        sender,
    };
}

export interface NativeInvoiceDocumentRenderOptions {
    payUrl?: string | null;
}

export function renderNativeInvoiceDocument(
    input: NativeInvoiceDocumentRenderInput,
    options: NativeInvoiceDocumentRenderOptions = {},
): NativeInvoiceDocumentRenderResult {
    const currency = input.currency;
    const visibility = getInvoiceTotalsVisibility({
        subtotal: input.subtotal,
        total: input.total,
        tax: input.tax,
        discount: input.discount,
        deposit: input.deposit,
    });
    const visible = applyNativeInvoicePdfFieldSettings({
        invoiceNumber: input.invoiceNumber,
        invoiceDate: input.invoiceDate,
        dueDate: input.dueDate,
        poNumber: input.poNumber,
        termTypeLabel: resolveNativeInvoiceTermLabel({
            termType: input.termType,
            customTerm: input.customTerm,
        }),
        terms: input.terms,
        note: input.note,
        from: input.from,
        to: input.to,
        labelOverrides: input.labelOverrides,
        fieldSettings: input.fieldSettings,
        senderEmail: input.senderEmail,
        isTaxPerLineItem: input.isTaxPerLineItem,
        isDiscountPerLineItem: input.isDiscountPerLineItem,
        isDepositPerLineItem: input.isDepositPerLineItem,
        isTaxDisplayedPerLineItem: input.isTaxDisplayedPerLineItem,
        isDiscountDisplayedPerLineItem: input.isDiscountDisplayedPerLineItem,
        isDepositDisplayedPerLineItem: input.isDepositDisplayedPerLineItem,
    });
    const invoiceNumber = visible.invoiceNumber ?? "";
    const invoiceDate = formatInvoiceDate(visible.invoiceDate);
    const dueDate = formatInvoiceDate(visible.dueDate);
    const poNumber = visible.poNumber ?? "";
    const termTypeLabel = visible.termTypeLabel ?? "";
    const from = buildPartyView({
        party: visible.from,
        showLabel: visible.showFromLabel,
        sender: visible.sender,
        addressLabels: visible.fromAddressLabels,
    });
    const to = buildPartyView({
        party: visible.to,
        showLabel: visible.showToLabel,
        sender: "",
        addressLabels: visible.toAddressLabels,
    });
    const lines: NativeInvoiceDocumentLineView[] = input.lines.map((line) => ({
        productOrService: line.productOrService ?? "",
        description: line.description ?? "",
        quantity:
            line.quantity == null ? "" : formatInvoiceQuantity(line.quantity),
        unitAmountFormatted:
            line.unitAmount == null
                ? ""
                : formatInvoiceMoney(line.unitAmount, currency),
        amountFormatted: formatInvoiceMoney(line.amount, currency),
        taxFormatted: formatInvoiceMoney(line.tax ?? 0, currency),
        discountFormatted: formatInvoiceMoney(line.discount ?? 0, currency),
        depositFormatted: formatInvoiceMoney(line.deposit ?? 0, currency),
    }));
    const pageSize = input.pageSize ?? NATIVE_INVOICE_DEFAULT_PAGE_SIZE;
    const payUrl = options.payUrl ?? null;
    const showPagination =
        input.showPagination ?? NATIVE_INVOICE_DEFAULT_SHOW_PAGINATION;
    const itemsTableVisibility = visible.itemsTableVisibility;
    const labels = resolveNativeInvoiceLabels(visible.labelOverrides);
    // Pass each shown meta field's label + value (not just a count) so
    // pagination can budget for labels/values that wrap in the narrow info-grid
    // column - e.g. a long custom Terms value or an overridden field label -
    // instead of assuming every field fits on one line.
    const metaFields = [
        { label: labels.invoiceNumber, value: invoiceNumber },
        { label: labels.poNumber, value: poNumber },
        { label: labels.termsType, value: termTypeLabel },
        { label: labels.invoiceDate, value: invoiceDate },
        { label: labels.dueDate, value: dueDate },
    ].filter((field) => Boolean(field.value));
    const pages = paginateNativeInvoiceDocument({
        pageSize,
        lines,
        footer: input.footer ?? "",
        showPagination,
        metaFields,
        fromLineCount: countNativeInvoicePartyLines(from),
        toLineCount: countNativeInvoicePartyLines(to),
        showSubtotal: visibility.showSubtotal && itemsTableVisibility.subtotal,
        showTax: visibility.showTax && itemsTableVisibility.tax,
        showDiscount: visibility.showDiscount && itemsTableVisibility.discount,
        showDeposit: visibility.showDeposit && itemsTableVisibility.deposit,
        showTotalDue: itemsTableVisibility.totalDue,
        terms: visible.terms ?? "",
        note: visible.note ?? "",
        hasPayButton: Boolean(payUrl),
    });
    const pageBox = nativeInvoicePageBoxCss(pageSize);
    const accentColor = getInvoiceAccentColor(
        input.backgroundColor ?? null,
        input.accentColor,
    );
    const backgroundColor = input.backgroundColor
        ? normalizeHexColor(input.backgroundColor)
        : "#ffffff";
    const textColor = getInvoiceTextColor(input.textColor);
    const mutedTextColor = getInvoiceMutedTextColor(textColor);
    const subtleTextColor = getInvoiceSubtleTextColor(textColor);
    const borderColor = getInvoiceBorderColor(textColor);
    const fontFamily = getInvoiceFontStack(input.fontFamily ?? null);
    const monogram =
        input.monogram ??
        input.initials ??
        deriveInvoiceMonogram(input.from.businessName);
    const monogramBackground = input.backgroundColor ? "#ffffff" : "#f5f6f8";
    const pageHtml = pages
        .map((page) =>
            renderNativeInvoiceDocumentPage({
                page,
                labels,
                logoUrl: input.logoUrl ?? null,
                monogram,
                accentColor,
                invoiceNumber,
                poNumber,
                termTypeLabel,
                invoiceDate,
                dueDate,
                from,
                to,
                showSubtotal:
                    visibility.showSubtotal && itemsTableVisibility.subtotal,
                showTax: visibility.showTax && itemsTableVisibility.tax,
                showDiscount:
                    visibility.showDiscount && itemsTableVisibility.discount,
                showDeposit:
                    visibility.showDeposit && itemsTableVisibility.deposit,
                showTotalDue: itemsTableVisibility.totalDue,
                itemsTableColumns: {
                    item: itemsTableVisibility.item,
                    quantity: itemsTableVisibility.quantity,
                    rate: itemsTableVisibility.rate,
                    amount: itemsTableVisibility.amount,
                    tax: itemsTableVisibility.taxColumn,
                    discount: itemsTableVisibility.discountColumn,
                    deposit: itemsTableVisibility.depositColumn,
                },
                subtotalFormatted: formatInvoiceMoney(input.subtotal, currency),
                taxFormatted: formatSignedMoney(
                    input.tax,
                    input.taxSign ?? "positive",
                    currency,
                ),
                discountFormatted: formatSignedMoney(
                    input.discount,
                    input.discountSign ?? "positive",
                    currency,
                ),
                depositFormatted: formatSignedMoney(
                    input.deposit,
                    input.depositSign ?? "positive",
                    currency,
                ),
                totalFormatted: formatInvoiceMoney(input.total, currency),
                terms: visible.terms ?? "",
                note: visible.note ?? "",
                payUrl,
                footerAlign: footerAlignCss(input.footerAlign),
                showPagination,
                showHeading: visible.showHeading,
            }),
        )
        .join("");

    return {
        html: `<!doctype html>
<html>
    <head>
        <meta charset="utf-8" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link rel="stylesheet" href="${INVOICE_PDF_FONTS_STYLESHEET_URL}" />
        <style>
${nativeInvoiceDocumentCss({
    pageBox,
    backgroundColor,
    fontFamily,
    monogramBackground,
    accentColor,
    textColor,
    mutedTextColor,
    subtleTextColor,
    borderColor,
})}
        </style>
    </head>
    <body>
        ${pageHtml}
    </body>
</html>`,
        pageCount: pages.length,
        pageBox,
    };
}
